import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Grid, Icon, Stack, Typography } from '@mui/material'
import i18n from 'simple-react-i18n'
import { groupBy, isNil, orderBy } from 'lodash'
import moment from 'moment'
import useActions from '../../../utils/customHook/useActions'
import { getDateWithHour } from '../../../utils/DateUtil'
import { normalizeString } from '../../../utils/StringUtil'
import ContactAction from '../../../referencial/components/contact/actions/ContactAction'
import ConfirmModal from '../../../components/modal/ConfirmModal'
import Input from '../../../components/forms/Input'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import SuperMultiAutocomplete from '../../../components/forms/SuperMultiAutocomplete'
import RpqsEventCard from './RpqsEventCard'
import RpqsEventDialog from './RpqsEventDialog'
import { EVENT_SOURCE_ICONS, getEventKey, getEventSource, getEventSourceOptions } from './RpqsEventsUtils'
import { lightGrey } from '../../../utils/constants/ColorTheme'
import { rpqsEventShape } from '../../constants/RpqsShapes'

const getEventTimestamp = event => getDateWithHour(event.date, event.hour)?.valueOf()

const filterEvents = (events, filter, getLabel) => {
    const searchValue = normalizeString(filter.searchValue)
    const endDate = isNil(filter.endDate) ? undefined : moment(filter.endDate).endOf('day').valueOf()
    return events.filter(event => {
        const timestamp = getEventTimestamp(event)
        const searchedText = normalizeString(`${event.comment ?? ''} ${getLabel(event) || ''} ${event.updateLogin ?? ''}`)
        return (!searchValue || searchedText.includes(searchValue))
            && (isNil(filter.startDate) || timestamp >= filter.startDate)
            && (isNil(endDate) || timestamp <= endDate)
            && (!filter.sources?.length || filter.sources.includes(getEventSource(event)))
    })
}

// Sous-groupes d'une année par libellé d'origine, dans l'ordre du plus récent événement de chacun.
// Sans libellé (RPQS d'une EG, fiches collectivité / EG), un seul groupe sans sous-bandeau.
const getOriginGroups = (events, getLabel) => {
    const groups = groupBy(events, e => getLabel(e) || '')
    return Object.keys(groups).map(label => ({ label, events: groups[label] }))
}

// Liste d'événements + popin de saisie, commune aux RPQS et aux fiches collectivité / EG.
// onSave et onDelete renvoient la promesse de l'action (json du back).
const RpqsEventsList = ({
    events = [],
    newEvent = {},
    managementUnits = [],
    canChooseManagementUnit = false,
    showSourceFilter = false,
    getOriginLabel,
    onSave = () => Promise.resolve(),
    onDelete = () => Promise.resolve(),
}) => {
    const dispatch = useDispatch()

    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const [selectedEvent, setSelectedEvent] = useState()
    const [openDialog, setOpenDialog] = useState(false)
    const [eventToDelete, setEventToDelete] = useState()
    const [tmpFilter, setTmpFilter] = useState({})
    const [filter, setFilter] = useState({})

    useEffect(() => {
        dispatch(ContactAction.fetchContacts())
    }, [dispatch])

    const openEvent = event => {
        setSelectedEvent(event)
        setOpenDialog(true)
    }

    useActions(() => ({
        new: () => openEvent(newEvent),
    }), [newEvent])

    const onValidate = event => {
        onSave(event).then(json => {
            if (json?.insert || json?.update) setOpenDialog(false)
        })
    }

    const onConfirmDelete = () => {
        onDelete(eventToDelete).then(json => {
            setEventToDelete(undefined)
            if (json?.delete) setOpenDialog(false)
        })
    }

    const getLabel = event => !!getOriginLabel && getOriginLabel(event)

    // Un bandeau par année, de la plus récente à la plus ancienne.
    const eventsByYear = useMemo(() => {
        const sortedEvents = orderBy(filterEvents(events, filter, getLabel), e => getEventTimestamp(e) ?? 0, 'desc')
        const groups = groupBy(sortedEvents, e => (e.date ? moment(e.date).year() : i18n.unknown))
        return orderBy(Object.keys(groups), key => (key === i18n.unknown ? -1 : parseInt(key)), 'desc').map(year => ({ year, events: groups[year] }))
    }, [events, filter, getOriginLabel])

    const changeFilter = changes => setTmpFilter(prev => ({ ...prev, ...changes }))

    return (
        <Grid container justifyContent='center' sx={{ padding: 1 }}>
            <Grid size={8}>
                <Stack spacing={1}>
                    <Card>
                        <CardContent>
                            <Grid container spacing={1} alignItems='center'>
                                <Grid size={showSourceFilter ? 3 : 4}>
                                    <Input
                                        title={i18n.search}
                                        value={tmpFilter.searchValue}
                                        onChange={searchValue => changeFilter({ searchValue })}
                                        onEnterKeyPress={searchValue => setFilter({ ...tmpFilter, searchValue })}
                                    />
                                </Grid>
                                <Grid size={showSourceFilter ? 3 : 4}>
                                    <SimpleDatePicker
                                        label={i18n.startDate}
                                        value={tmpFilter.startDate}
                                        onChange={startDate => changeFilter({ startDate })}
                                    />
                                </Grid>
                                <Grid size={showSourceFilter ? 3 : 4}>
                                    <SimpleDatePicker
                                        label={i18n.endDate}
                                        value={tmpFilter.endDate}
                                        onChange={endDate => changeFilter({ endDate })}
                                    />
                                </Grid>
                                {showSourceFilter && (
                                    <Grid size={3}>
                                        <SuperMultiAutocomplete
                                            label={i18n.eventTypes}
                                            options={getEventSourceOptions()}
                                            values={tmpFilter.sources}
                                            onChange={sources => changeFilter({ sources })}
                                            multiple
                                        />
                                    </Grid>
                                )}
                                <Grid container size={12} spacing={1} justifyContent='flex-end'>
                                    <Button
                                        variant='outlined'
                                        onClick={() => {
                                            setTmpFilter({})
                                            setFilter({})
                                        }}
                                    >
                                        {i18n.reinit}
                                    </Button>
                                    <Button variant='contained' color='primary' onClick={() => setFilter(tmpFilter)}>
                                        {i18n.search}
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    {eventsByYear.length ? eventsByYear.map(group => (
                        <Accordion key={group.year} defaultExpanded>
                            <AccordionSummary>
                                {`${group.year} (${group.events.length} ${group.events.length > 1 ? i18n.elements : i18n.element})`}
                            </AccordionSummary>
                            <AccordionDetails>
                                {getOriginGroups(group.events, getLabel).map(originGroup => (
                                    <Grid key={originGroup.label || 'all'} container size={12}>
                                        {!!originGroup.label && (
                                            <Grid size={12} container spacing={2} alignItems='center' sx={{ backgroundColor: lightGrey, padding: 1 }}>
                                                <Icon>{EVENT_SOURCE_ICONS[getEventSource(originGroup.events[0])]}</Icon>
                                                {`${originGroup.label} (${originGroup.events.length} ${originGroup.events.length > 1 ? i18n.events : i18n.event})`}
                                            </Grid>
                                        )}
                                        <Grid size={12}>
                                            {originGroup.events.map(event => (
                                                <RpqsEventCard
                                                    key={getEventKey(event)}
                                                    event={event}
                                                    contacts={contacts}
                                                    originLabel={getLabel(event)}
                                                    onClick={() => openEvent(event)}
                                                />
                                            ))}
                                        </Grid>
                                    </Grid>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    )) : (
                        <Typography align='center'>{i18n.noEvents}</Typography>
                    )}
                </Stack>
            </Grid>
            <RpqsEventDialog
                open={openDialog}
                event={selectedEvent}
                contacts={contacts}
                managementUnits={managementUnits}
                canChooseManagementUnit={canChooseManagementUnit}
                onClose={() => setOpenDialog(false)}
                onValidate={onValidate}
                onDelete={setEventToDelete}
            />
            <ConfirmModal
                isOpen={!isNil(eventToDelete)}
                title={i18n.sureDeleteRpqsEvent}
                onValidate={onConfirmDelete}
                onClose={() => setEventToDelete(undefined)}
            />
        </Grid>
    )
}

RpqsEventsList.propTypes = {
    events: PropTypes.arrayOf(rpqsEventShape),
    newEvent: rpqsEventShape,
    managementUnits: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    canChooseManagementUnit: PropTypes.bool,
    showSourceFilter: PropTypes.bool,
    getOriginLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
}

export default RpqsEventsList
